import { isInMatch } from '../../domain/machine';
import type { MatchRow, StatusOverride } from '../../types';

/**
 * B-15 — the **hypothesis detail sheet's pure decisions** (design spec §2.4 detail-sheet arm;
 * scenarios 10 and 17). DOM-free so the Node suite drives every arm; the sheet component is glue
 * over these plus the engine's own facades (`app/annotations`, `app/lockedBoard.setTileOverride`,
 * `app/recorder.holdVoiceNote` — reached there, never re-derived here).
 *
 * ── What is deliberately NOT decided here ───────────────────────────────────
 *
 * **The annotation window.** `commands/annotations.ts` keeps `WRITABLE_IN` private
 * (firstHalf → distill) and that is right: a copy of it in the UI layer would be exactly the
 * near-duplicate the reuse contract's detector exists to catch, and the two would drift the first
 * time either was re-ruled. The sheet needs no copy because **every state it is reachable from is
 * inside that window by construction** — the capture surfaces mount only while `isInMatch` and
 * decline every contact before kickoff (`canCapture`), and the workflow's Step-1 entry point runs
 * at `halftime`/`fullTime`. The engine's rejection remains the backstop, rendered as an ordinary
 * sentence rather than prevented by a second rule.
 */

/** One status-override control (§2.4: *disproved / invalidated / clear*, reversible). */
export interface OverrideChoiceB {
  readonly value: StatusOverride;
  readonly label: string;
  /** Stable suffix for the control's `data-testid` — `null` has no usable string form. */
  readonly key: string;
  /** What the button does, in the user's words. */
  readonly hint: string;
}

/**
 * The three controls, in reading order — set, set, undo.
 *
 * `satisfies` proves every listed value is a real `StatusOverride`; the `_EveryOverrideOffered`
 * check below proves none is **missing**, which is the failure with no runtime symptom (a picker
 * offering two of three override values simply makes the third unreachable, with no error
 * anywhere — the `PHASE_OPTIONS` argument, one union over).
 */
export const OVERRIDE_CHOICES = [
  {
    value: 'disproved',
    label: 'Disproved',
    key: 'disproved',
    hint: 'Watched for it and it is not happening.',
  },
  {
    value: 'invalidated',
    label: 'Invalidated',
    key: 'invalidated',
    hint: 'It stopped applying — a sub, a shape change.',
  },
  { value: null, label: 'Clear', key: 'clear', hint: 'Back to tracking. The count never changes.' },
] as const satisfies readonly OverrideChoiceB[];

type _EveryOverrideOffered = Exclude<StatusOverride, (typeof OVERRIDE_CHOICES)[number]['value']>;
const _everyOverrideOffered: _EveryOverrideOffered[] = [];
void _everyOverrideOffered;

/**
 * §2.4 — is the **Hold to talk** voice note offerable on this match right now?
 *
 * `recorder.holdVoiceNote` writes an `Observation`, so the recorder refuses outside the capture
 * window (`pressOrStart` → `refused`) exactly as the Flag control does. This mirrors that window
 * from the engine's own `isInMatch` — imported, never re-derived — for the same reason B-9's
 * `canAddPattern` exists: a control that renders only to be refused is a mistap manufactured on
 * chrome. The recorder stays the authority; this decides only whether to draw the button.
 */
export function voiceNoteAvailableB(match: MatchRow): boolean {
  return isInMatch(match.state);
}

/** Trimmed text, or `null` when there is nothing to save (`ERR_ANNOTATION_EMPTY`'s UI half). */
export function annotationTextB(draft: string): string | null {
  const trimmed = draft.trim();
  return trimmed === '' ? null : trimmed;
}
